import { rem } from 'polished'
import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Divider from 'LuxKit/Divider'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import { getCanAgentExportMedia } from 'agentHub/selectors/agentHubSelectors'
import ExportMediaContentButton from 'components/Common/ExportMediaContentButton/ExportMediaContentButton'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import PerfectForGroupsLabel from 'components/Common/Labels/PerfectForGroupsLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import type { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ContentTagLabels from 'components/Content/ContentTagLabels'
import CharterBundleBanner from 'components/OfferPage/CharterBundle/CharterBundleBanner'
import ContactUSP from 'components/OfferPage/ContactUSP'
import OfferBNPLAd from 'components/OfferPage/OfferBNPLAd'
import OfferPageTrustPilot from 'components/OfferPage/OfferPageTrustPilot'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { NON_CHARTER_TO_CHARTER_OFFER_MAP } from 'constants/charterFlight'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import CurrencyContext from 'contexts/currencyContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { buildAgentHubQuotingURL } from 'lib/agentHub/quoting'
import { shouldShowPerfectForGroupsLabel } from 'lib/charterFlights/kidsFlyFreeUtils'
import { hasSelectedRooms, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { shouldShowSalesUSP } from 'selectors/featuresSelectors'
import {
  getPropertyCheapestRates,
  getPropertyHotelOnlyRates,
  getPropertyOfferMapping,
} from 'selectors/offerPage/offerPageSelectors'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import { SiteTakeoverCampaignTagLabel } from 'takeovers/utils/components/SiteTakeoverCampaignTagLabel'
import LuxPlusMonthOfferPageNotice from 'takeovers/luxPlusMonthTakeover/components/LuxPlusMonthOfferPageNotice'
import { LUX_PLUS_MONTH_CAMPAIGN_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import BookmarkButton from 'wishlists/components/Bookmark/Buttons/BookmarkButton'
import WishlistGuard from 'wishlists/components/WishlistGuard'

import PropertyPromotionLabel from './PropertyPromotionLabel'
import PropertyPropertyDetails from './PropertyPropertyDetails'
import PropertySummary from './PropertySummary'

const OfferIntroWrapper = styled(LayoutContainer)`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(40)};
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(30)} ${rem(12)};
  }
`

const OfferIntroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }

  ${mediaQueryUp.desktop} {
    grid-gap: ${rem(16)} ${rem(40)};
    grid-template-columns: 1fr min-content;
    grid-template-rows: min-content min-content min-content 1fr;
  }
`

const SummaryBlock = styled(Group)`
  ${mediaQueryUp.tablet} {
    position: sticky;
    /* height of the filter bar */
    top: ${rem(64)};
    align-self: flex-start;
    grid-row: 3;
    grid-column: 2;
  }

  ${mediaQueryUp.desktop} {
    grid-row: 2 / span 3;
    grid-column: 2;
  }
`

const Summary = styled(PropertySummary)`
  display: block;
  align-self: flex-start;
  min-width: ${rem(290)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(370)};
  }
`

const OfferOtherActions = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    gap: ${rem(8)};
    justify-content: flex-end;
    align-items: center;
    grid-row: 1;
    grid-column: 2;
  }
`

const MobileBNPLAd = styled(OfferBNPLAd)`
  margin-top: ${rem(24)};
  padding-top: ${rem(24)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  order: 1;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const ContentsCSSBreakpoint = styled(CSSBreakpoint)`
  display: contents;
`

const CenteredLabelGroup = styled(LabelGroup)`
  align-items: center;
`

interface Props {
  property: App.Property.Property
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  offer: App.HotelOffer
  cheapestPropertyRates: [App.EnrichedPropertyRate | App.Property.AnytimeRate | undefined, App.EnrichedPropertyRate | undefined]
  onViewPackageOptions: () => void
  shouldShowSalesPhoneUSP: boolean
  shareConfig: ShareConfig
  propertyRates: Array<App.EnrichedPropertyRate>
  duration: number
  checkInDate?: string
  checkoutDate?: string
  regionCode: string
  isUltraLux: boolean
  isOccupancyContextKnown: boolean
  rooms: Array<App.Occupants>
}

function PropertyIntro(props: Props) {
  const {
    property,
    offer,
    cheapestPropertyRates,
    onViewPackageOptions,
    shouldShowSalesPhoneUSP,
    shareConfig,
    propertyRates,
    duration,
    checkInDate,
    checkoutDate,
    regionCode,
    isUltraLux,
    isOccupancyContextKnown,
    rooms,
  } = props

  const intl = useIntl()
  const currency = useContext(CurrencyContext)

  const agentHubMarketingPromoUrl = config.agentHub.isEnabled && config.EXPORT_MEDIA_CONTENT_ENABLED && getCanAgentExportMedia
    ? buildAgentHubQuotingURL(offer.id, { currency, region: regionCode })
    : undefined
  const shareWithFriendsIsLastButton = !config.ENABLE_TRIP_PLANNER && !agentHubMarketingPromoUrl

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])

  const shouldShowToolTip = useScreenSizeUp('tablet')

  const [cheapestRate] = cheapestPropertyRates
  const cheapestRatePrice = cheapestRate && 'service' in cheapestRate
    ? cheapestRate.totals.sellingPrice
    : offer.lowestPricePackage?.price ?? 0

  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const showTasteOfGoldPromo = isTasteOfGoldActive && isTasteOfGoldEligibleOffer(
    offer,
    currency,
    property.location.countryCode?.toLowerCase() !== 'au',
    cheapestRatePrice,
  )

  return (
    <OfferIntroWrapper>

      <VerticalSpacer gap={16}>
        <OfferIntroGrid data-testid="offer-intro">
          <CenteredLabelGroup>
            {property.promotion?.endIsoDateTime && <>
              <PropertyPromotionLabel promotion={property.promotion} />
              <OfferUrgencyCountdownLabel
                endDate={property.promotion.endIsoDateTime}
                tagPlace="property-offer-intro"
              />
            </>}
            {property.isAgentHubExclusive && <PromotionLabel key="agenthub_exclusive">
              {intl.formatMessage({
                id: 'AFcvjM-pA2ndFwONPek5',
                defaultMessage: 'Agent Exclusive',
              })}
            </PromotionLabel>}
            {shouldShowPerfectForGroupsLabel(offer) && <PerfectForGroupsLabel />}
            <ContentTagLabels offerId={offer.id} page="offer" />
            {offer.campaignTags?.filter(campaignTag => campaignTag === LUX_PLUS_MONTH_CAMPAIGN_ID).map(campaignTag => <SiteTakeoverCampaignTagLabel
              key={campaignTag}
              campaignTagId={campaignTag}
              onOfferCard
              onOfferPage
              offer={offer}
            />)}
            <LuxPlusMonthOfferPageNotice offer={offer} />
          </CenteredLabelGroup>
          <OfferOtherActions>
            <ShareModal shareConfig={shareConfig} openOnHash="#share">
              <ShareWithFriendsButton
                horizontalOutdent={shareWithFriendsIsLastButton ? 'end' : undefined}
                showToolTip={shouldShowToolTip}
              />
            </ShareModal>
            <WishlistGuard>
              <BookmarkButton offer={offer} />
            </WishlistGuard>
            {agentHubMarketingPromoUrl && <ExportMediaContentButton
              href={agentHubMarketingPromoUrl}
              target="_blank"
            />}
          </OfferOtherActions>
          <PropertyPropertyDetails
            offerId={offer.id}
            property={property}
            propertyRates={propertyRates}
            duration={duration}
            checkInDate={checkInDate}
            checkoutDate={checkoutDate}
            isUltraLux={isUltraLux}
            showTasteOfGoldPromo={showTasteOfGoldPromo}
            isOccupancyContextKnown={isOccupancyContextKnown}
            rooms={rooms}
          />
          <ContentsCSSBreakpoint min="tablet">
            <SummaryBlock direction="vertical" gap={12}>
              <div>
                <Summary
                  property={property}
                  lpcOffer={offer}
                  onViewPackageOptions={onViewPackageOptions}
                />
              </div>
              <OfferPageTrustPilot />
              <OfferBNPLAd prices={allPrices} />
            </SummaryBlock>
          </ContentsCSSBreakpoint>
          <MobileBNPLAd prices={allPrices} />
        </OfferIntroGrid>
        {NON_CHARTER_TO_CHARTER_OFFER_MAP[offer.id] && <>
          <Divider kind="primary" />
          <CharterBundleBanner offer={offer} />
        </>}
        {shouldShowSalesPhoneUSP && <ContactUSP />}
      </VerticalSpacer>
    </OfferIntroWrapper>
  )
}

type MappedProps = Pick<Props,
  | 'cheapestPropertyRates'
  | 'shouldShowSalesPhoneUSP'
  | 'propertyRates'
  | 'duration'
  | 'checkInDate'
  | 'checkoutDate'
  | 'regionCode'
  | 'isUltraLux'
  | 'isOccupancyContextKnown'
  | 'rooms'
> & { open: boolean }

export default withOfferPageAndReduxState((state, appState, ownProps: Omit<Props, keyof MappedProps>): MappedProps => {
  const hasPromotions = !!ownProps.property.promotion?.endIsoDateTime
  // Checking against `config.LPC_TACTICAL` is a bit dubious, but we're following
  // `<BedbankPropertyDetails/>`'s lead. We _think_ the intent is to only show `<ContactUSP/>` for
  // the LE brand, and not any of the white labels. We can revisit using a more appropriate config
  // flag later.
  const shouldShowUSPForPromotions = hasPromotions && config.LPC_TACTICAL

  return {
    cheapestPropertyRates: getPropertyCheapestRates(state, appState),
    shouldShowSalesPhoneUSP: shouldShowSalesUSP(appState, state.offer?.lowestPricePackage?.trackingPrice ?? 0, 300) || shouldShowUSPForPromotions,
    propertyRates: getPropertyHotelOnlyRates(state, appState),
    duration: state.duration,
    checkInDate: state.checkInDate,
    checkoutDate: state.checkOutDate,
    regionCode: appState.geo.currentRegionCode,
    isUltraLux: getPropertyOfferMapping(state, appState)?.isUltraLux ?? false,
    isOccupancyContextKnown: hasSelectedRooms(state.rooms),
    rooms: state.rooms,
    open: !!state.activeFilter,
  }
}, PropertyIntro, preventRenderWithOpenFilter)
